if (!customElements.get('variant-selector')) {
    class VariantSelector extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.elData = this.querySelector('[data-variants]');
            this.elOptionValues = this.querySelectorAll('[data-option-values]');
            this.elValues = this.querySelectorAll('[data-value]');

            this.variants = JSON.parse(this.elData.textContent);
            this.currentVariant =
                this.variants.find((variant) => variant.selected) || this.variants[0];
            this.currentOptionValues = { ...this.currentVariant.options };

            this.variantImagePreloads = new Set();
            this.variantImagesPreloaded = false;
            this.variantImagesPreloadStarted = false;

            this.elValues.forEach((el) => (el.onchange = this.setCurrentVariant.bind(this)));

            this.elOptionValues.forEach((el) => {
                const collapsed = el.getAttribute('data-collapsed') == 'true';
                if (collapsed) {
                    const elMore = el.querySelector('[data-more]');
                    elMore.addEventListener('click', this.showMoreValues.bind(this));
                }
            });

            if (this.variantImagesPreloadStarted) return;
            this.variantImagesPreloadStarted = true;

            const startPreload = () => {
                const preload = this.preloadVariantImages.bind(this);
                window.requestIdleCallback
                    ? window.requestIdleCallback(preload, { timeout: 3000 })
                    : setTimeout(preload, 2000);
            };

            document.readyState === 'complete'
                ? startPreload()
                : window.addEventListener('load', startPreload, { once: true });
        }

        preloadVariantImages() {
            const urlsToLoad = [];

            this.variants.forEach((variant) => {
                const { media } = variant;

                media.forEach((media) => {
                    const urls = media.urls && media.urls.length ? media.urls : [media.src];

                    urls.forEach((url) => {
                        if (this.variantImagePreloads.has(url)) return;
                        this.variantImagePreloads.add(url);
                        urlsToLoad.push(url);
                    });
                });
            });

            const loadPromises = urlsToLoad.map(
                (url) =>
                    new Promise((resolve) => {
                        const img = new Image();
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                        img.src = url;
                    }),
            );

            Promise.all(loadPromises).then(() => {
                this.variantImagesPreloaded = true;
            });
        }

        findMatchingVariant(optionValues) {
            return this.variants.find((variant) =>
                Object.entries(variant.options).every(
                    ([key, value]) => optionValues[key] === value,
                ),
            );
        }

        setCurrentVariant(ev) {
            const elOptionValue = ev.currentTarget;
            const elOption = elOptionValue.closest('[data-option]');
            const elOptionTitle = elOption && elOption.querySelector('[data-option-title]');
            const elOptionSubtitle = elOption && elOption.querySelector('[data-option-subtitle]');

            const optionId = elOptionValue.getAttribute('data-option-id');
            const { value } = elOptionValue;
            const { title, subtitle } = elOptionValue.dataset;
            const previousOptionValues = { ...this.currentOptionValues };

            this.currentOptionValues[optionId] = value;
            this.currentVariant = this.findMatchingVariant(this.currentOptionValues);

            if (!this.currentVariant) {
                this.currentOptionValues = previousOptionValues;
                const previousValue = previousOptionValues[optionId];
                const previousInput = [...this.elValues].find(
                    (el) =>
                        el.getAttribute('data-option-id') === optionId &&
                        el.value === previousValue,
                );
                previousInput && (previousInput.checked = true);
                return;
            }

            if (elOptionTitle) {
                elOptionTitle.innerHTML = title || value;
            }
            if (elOptionSubtitle) {
                elOptionSubtitle.innerHTML = subtitle || '';
            }
            this.updateChildImages();
            this.dispatchEvent(new CustomEvent('variant:changed', { detail: this.currentVariant }));
        }

        updateChildImages() {
            const { child_images } = this.currentVariant;
            if (!child_images) return;

            Object.entries(child_images).forEach(([optionId, tag]) => {
                if (!tag) return;

                const elOption = this.querySelector(`[data-option="${optionId}"]`);
                const elProduct = elOption && elOption.closest('.bundle-selector--product');
                const elOptionImage = elProduct && elProduct.querySelector('[data-option-image]');
                if (elOptionImage) elOptionImage.innerHTML = tag;
            });
        }

        showMoreValues(ev) {
            const elMore = ev.currentTarget;
            const elOption = elMore.closest('[data-option-values]');
            const elValues = elOption.querySelectorAll('[data-value]');

            const isExpanded = elMore.getAttribute('aria-expanded') == 'true';
            elMore.setAttribute('aria-expanded', !isExpanded);

            if (isExpanded) {
                elMore.classList.remove('active');
                elOption.classList.remove('expanded');
                elOption.classList.add('collapsed');

                const hiddenValues = [...elValues].filter((el) =>
                    el.parentNode.classList.contains('value-hidden'),
                );
                elOption.ontransitionend = () => {
                    hiddenValues.forEach((el) => el.parentNode.setAttribute('aria-hidden', true));
                    elOption.ontransitionend = null;
                };
            } else {
                elMore.classList.add('active');
                elValues.forEach((el) => el.parentNode.setAttribute('aria-hidden', false));
                setTimeout(() => {
                    elOption.classList.add('expanded');
                    elOption.classList.remove('collapsed');
                }, 50);
            }
        }
    }

    customElements.define('variant-selector', VariantSelector);
}
